/**
 * Pure state machine for clinician-activity telemetry.
 *
 * The React tracker (ClinicianActivityTracker) owns timers, DOM listeners, and
 * transport; this class owns only the counters and the heartbeat buffer.
 *
 * Active EHR time is reconstructed downstream as the UNION of
 * `[ts, ts + HEARTBEAT_WINDOW_MS)` intervals per (clinician, document) — never
 * the sum, so a clinician with the same document open in two tabs is not
 * double-counted.
 */

export const HEARTBEAT_WINDOW_MS = 5_000

const MAX_BUFFERED_EVENTS = 600

export interface BufferedHeartbeat {
  id: string
  ts: string
  pathname: string
  clicks: number
  keys: number
}

export type ActivityInputKind = 'click' | 'key' | 'other'

export class ActivityCollector {
  private clicks = 0
  private keys = 0
  private otherInputs = 0
  private windowStartedAt: string | null = null
  private buffer: BufferedHeartbeat[] = []

  recordInput(kind: ActivityInputKind, nowIso: string): void {
    this.windowStartedAt ??= nowIso
    if (kind === 'click') {
      this.clicks++
    } else if (kind === 'key') {
      this.keys++
    } else {
      this.otherInputs++
    }
  }

  closeWindow(pathname: string): void {
    if (
      this.clicks + this.keys + this.otherInputs > 0 &&
      this.windowStartedAt !== null
    ) {
      this.enqueue({
        id: crypto.randomUUID(),
        ts: this.windowStartedAt,
        pathname,
        clicks: this.clicks,
        keys: this.keys,
      })
    }
    this.clicks = 0
    this.keys = 0
    this.otherInputs = 0
    this.windowStartedAt = null
  }

  drain(): BufferedHeartbeat[] {
    const events = this.buffer
    this.buffer = []
    return events
  }

  requeue(events: BufferedHeartbeat[]): void {
    this.buffer = [...events, ...this.buffer].slice(-MAX_BUFFERED_EVENTS)
  }

  get size(): number {
    return this.buffer.length
  }

  private enqueue(event: BufferedHeartbeat): void {
    this.buffer.push(event)
    if (this.buffer.length > MAX_BUFFERED_EVENTS) {
      this.buffer = this.buffer.slice(-MAX_BUFFERED_EVENTS)
    }
  }
}
